/**
 * Register Component
 * 
 * Provides a user interface for creating a new account with email and password.
 * Handles form submission, validation, error display, and redirects to dashboard on successful registration.
 * 
 * Features:
 * - Email and password input validation
 * - Password confirmation check
 * - Error message display
 * - Loading state during authentication
 * - Link to login page
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { registerUser } from '../utilities/authUtils';
import AppLogo from '../../Common/Brand/AppLogo';
import FeedbackNotice from '../../Common/Feedback/FeedbackNotice';
import { DESIRED_JOBS_CONTEXT_ROUTE } from '../../../navigationRoutes';


const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreedToPrivacyPolicy, setAgreedToPrivacyPolicy] = useState(false);
  const [isOver18, setIsOver18] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  /**
   * Handle form submission for user registration
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validate input
      if (!email || !password || !confirmPassword) {
        setError('Please fill in all fields');
        setLoading(false);
        return;
      }

      // Check password match
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }

      // Check password length
      if (password.length < 6) {
        setError('Password must be at least 6 characters long');
        setLoading(false);
        return;
      }

      // Require privacy policy agreement
      if (!agreedToPrivacyPolicy) {
        setError('You must agree to the Privacy Policy before registering');
        setLoading(false);
        return;
      }

      // Require age confirmation
      if (!isOver18) {
        setError('You must confirm you are 18 years or older to participate');
        setLoading(false);
        return;
      }

      // Attempt registration
      const result = await registerUser(email, password);
      
      if (result.success) {
        // Store user info in localStorage for persistence
        localStorage.setItem('user', JSON.stringify({
          uid: result.uid,
          email: result.email,
        }));
        
        // Start new users at the first portfolio-context step.
        navigate(DESIRED_JOBS_CONTEXT_ROUTE, { replace: true });
      }
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
      console.error('Registration error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container align-content-center" style={{height: "100vh"}}>
      <div className="card p-4 m-auto" style={{ maxWidth: '400px'}}>
        <AppLogo className="auth-brand" size={128} linked />
        <h2 className="text-center">Create Account</h2>
        <p className="text-center text-muted small">
          After registering, we will guide you through desired jobs, skills, projects, and your first portfolio.
        </p>
        
        <FeedbackNotice message={error || ''} variant="danger" onDismiss={() => setError(null)} />
        
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label htmlFor="email" className="form-label">Email Address</label>
            <input
              type="email"
              className="form-control"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              disabled={loading}
            />
          </div>

          <div className="mb-3">
            <label htmlFor="password" className="form-label">Password</label>
            <input
              type="password"
              className="form-control"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password (min. 6 characters)"
              disabled={loading}
            />
          </div>

          <div className="mb-3">
            <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
            <input
              type="password"
              className="form-control"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              disabled={loading}
            />
          </div>

          <div className="mb-3 form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="ageConfirmation"
              checked={isOver18}
              onChange={(e) => setIsOver18(e.target.checked)}
              disabled={loading}
            />
            <label className="form-check-label" htmlFor="ageConfirmation">
              I confirm I am 18 years of age or older
            </label>
          </div>

          <div className="mb-3 form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="privacyPolicyAgreement"
              checked={agreedToPrivacyPolicy}
              onChange={(e) => setAgreedToPrivacyPolicy(e.target.checked)}
              disabled={loading}
            />
            <label className="form-check-label" htmlFor="privacyPolicyAgreement">
              I agree to the{' '}
              <Link to="/privacy-policy" className="text-decoration-none">
                Privacy Policy
              </Link>
            </label>
          </div>

          <button
            type="submit"
            className="btn btn-primary w-100"
            disabled={loading || !agreedToPrivacyPolicy || !isOver18}
          >
            {loading ? 'Creating account...' : 'Register'}
          </button>
        </form>

        <p className="mt-3 text-center">
          Already have an account?{' '}
          <Link to="/login" className="auth-link">
            Login here
          </Link>
        </p>

        <p className="text-center mb-0 small text-muted">
          <Link to="/privacy-policy" className="text-decoration-none">
            Privacy Policy
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
